'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSocketManagerContext } from '../providers/context';
import NatsManager from '../library/nats-manager';
import type { DataSource, MarketDepthData } from '../types';
import { NATS_EVENT_NAMES } from '../constants/nr-events';

/**
 * Options for useLivePrice hook
 */
export interface UseLiveDepthOptions {
  /** NATS subject path for subscribing (e.g., 'prices.NSE.RELIANCE') */
  natsSubjectPath?: string;
  /** Preferred data source. Defaults to 'nats'. Falls back to 'websocket' if natsSubjectPath is missing -- websocket market depth support is not there yet. */
  source?: DataSource;
  /** Whether the subscription is enabled */
  enabled?: boolean;
}

export function useLiveDepth({
  natsSubjectPath,
  source = 'nats',
  enabled = true,
}: UseLiveDepthOptions): MarketDepthData | null {
  const [depthData, setDepthData] = useState<MarketDepthData | null>(null);
  const context = useSocketManagerContext();
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const trackEvent = context.config.analytics?.trackEvent;

  /**
   * Handler for NATS price messages
   */
  const handleNatsMessage = useCallback(
    (data: unknown, error?: Error) => {
      if (error) {
        console.error('[useLiveDepth] NATS error:', error);
        trackEvent?.(NATS_EVENT_NAMES.HOOK_ERROR, {
          hook: 'useLiveDepth',
          natsSubjectPath,
          error: error.message,
        });
        return;
      }

      const message = data as Record<string, unknown>;

      setDepthData(message as MarketDepthData);
    },
    [trackEvent, natsSubjectPath],
  );

  /**
   * Effect: Subscribe to prices when connection is ready
   */
  useEffect(() => {
    // Cleanup previous subscription
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    // NATS subscription
    if (source === 'nats' && natsSubjectPath) {
      // First check if connection is ready from context
      if (!context.isNatsConnected) {
        // Connection not ready yet - this is expected during initialization
        return;
      }

      try {
        const natsManager = NatsManager.getInstance();
        unsubscribeRef.current = natsManager.subscribe(
          natsSubjectPath,
          handleNatsMessage,
        );
      } catch (error) {
        console.error('[useLiveDepth] Error subscribing to NATS:', error);
        trackEvent?.(NATS_EVENT_NAMES.HOOK_SUBSCRIBE_ERROR, {
          hook: 'useLiveDepth',
          natsSubjectPath,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }

    // Cleanup on unmount or dependency change
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [
    enabled,
    natsSubjectPath,
    source,
    context.isNatsConnected,
    handleNatsMessage,
    trackEvent,
  ]);

  /**
   * Effect: Update connection status in price data
   */
  useEffect(() => {
    const isConnected = context.isNatsConnected;

    setDepthData((prev) => ({
      ...prev,
      isConnected,
    }));
  }, [context.isPricesConnected, context.isNatsConnected]);

  return depthData;
}

export default useLiveDepth;
